import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {Text} from '@Components/text';
import {noop} from '@Utils/general.util';
import type {TabItemContentStorage, TabKey} from '@Components/tabs';
import {Tabs} from '@Components/tabs';
import {loadDesignSelector} from '@Libraries/poster-library';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {DesignGallery} from '@Components/design-gallery';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {DesignSelectorProps} from '@Components/design-selector/design-selector.types';
import {containerQuery, DesignSelectorTab, skeletonLoaderContainerQuery} from '@Components/design-selector/design-selector.types';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import {SkeletonBrick} from '../skeleton-brick';
import styles from './design-selector.module.scss';
import {useAppDispatch} from '@/hooks';
import '@Global/create-from-scratch';
import {createRoot} from 'react-dom/client';

export function DesignSelector({
  onDesignListChange = noop,
  selectedDesignIds = [],
  containerRef,
  onDesignSelectionCallback = noop,
  onTabSelected = noop,
  designSelectorTab = DesignSelectorTab.MY_DESIGNS,
  isLoading = false,
  startDesignFromUploadBtnOpts = undefined,
  designGalleryStartingURI = GalleryThemeURI.DEFAULT,
  hideTabContainer = false,
  mobileClasses = '',
  desktopClasses = '',
  designGalleryHeight = '80vh',
  showSelectionCountForMobile = false,
}: DesignSelectorProps): ReactElement {
  const isUserPAYG = window.PMW.isPAYGUser();
  const SKELETON_LOADER = 'js-design-selector-element-skeleton-loader';
  const DESIGN_SELECTOR_ELEMENT_TABS_ID = 'design-selector-element-tabs';
  const LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT = 'loadDesignsForDesignSelectorIfNotLoaded';
  const params = useContainerQuery(containerRef, containerQuery);
  const skeletonParams = useContainerQuery(containerRef, skeletonLoaderContainerQuery);
  const paramsRef = useRef(params);
  const [areMyDesignsLoaded, setAreMyDesignsLoaded] = useState(false);
  const [myDesignsLength, setMyDesignsLength] = useState(0);
  const [selectedDesignsList, setSelectedDesignsList] = useState<string[]>([]);
  const [areTeamSpacesDesignsLoaded, setAreTeamSpacesDesignsLoaded] = useState(false);
  const selectedDesignsArrayRef = useRef(selectedDesignsList);
  const dispatch = useAppDispatch();

  let skeletonLoaderHTML = document.querySelector(`.${SKELETON_LOADER}`);

  const isMyDesignsSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.MY_DESIGNS;
  };

  const updateTabSelection = (newTab: DesignSelectorTab): void => {
    onTabSelected(newTab);
  };

  const isTeamSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.TEAM_SPACES;
  };

  const isDesignGallerySelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES;
  };

  useEffect(() => {
    if (areSelectedDesignsIdentical()) {
      return;
    }

    const oldDesignIds = selectedDesignsArrayRef.current;
    selectedDesignsArrayRef.current = selectedDesignIds;
    updateStylesForNewDesignIds(oldDesignIds, selectedDesignIds);
    updateSelectors();
  }, [selectedDesignIds]);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const updateStylesForNewDesignIds = (oldDesignIds: string[], newDesignIds: string[]): void => {
    oldDesignIds.forEach((oldDesignId) => {
      if (!newDesignIds.includes(oldDesignId)) {
        removeStylesOnDeselection(oldDesignId);
      }
    });
  };

  const areSelectedDesignsIdentical = (): boolean => {
    if (selectedDesignIds.length !== selectedDesignsArrayRef.current.length) {
      return false;
    }

    for (let i = 0; i < selectedDesignIds.length; i++) {
      if (!selectedDesignsArrayRef.current[i]) {
        return false;
      }

      if (selectedDesignIds[i] !== selectedDesignsArrayRef.current[i]) {
        return false;
      }
    }

    return true;
  };

  const getLoaderHTML = (): Element | null => {
    if (skeletonLoaderHTML) {
      return skeletonLoaderHTML;
    }

    skeletonLoaderHTML = document.querySelector(`.${SKELETON_LOADER}`);
    return skeletonLoaderHTML;
  };

  const addSelectionStylesIfNeeded = (): void => {
    if (!selectedDesignIds) {
      return;
    }

    selectedDesignIds.forEach((designId) => {
      addStylesOnSelection(designId);
      updateSelectors();
    });
  };

  useEffect(() => {
    if (!areMyDesignsLoaded) {
      loadMyDesignsDesignSelector(() => {
        hideLoader();
        setAreMyDesignsLoaded(true);
        setMyDesignsLength(getMyDesignsLength());
        selectedDesignsArrayRef.current = selectedDesignIds;
        addSelectionStylesIfNeeded();

        if (isMobile()) {
          addCheckIconToAllDesigns();
        }
      });
    }
  }, []);

  const shouldShowLoader = (): boolean => {
    if (isMyDesignsSelected() && !areMyDesignsLoaded) {
      return true;
    }

    if (isTeamSelected() && !areTeamSpacesDesignsLoaded) {
      return true;
    }

    return false;
  };

  const showLoader = (): void => {
    getLoaderHTML()?.classList.remove('_hidden');
  };

  const hideLoader = (): void => {
    getLoaderHTML()?.classList.add('_hidden');
  };

  const isLoaderVisible = (): boolean => {
    return !getLoaderHTML()?.classList.contains('_hidden');
  };

  useDidUpdateEffect(() => {
    dispatch(selectTab(DESIGN_SELECTOR_ELEMENT_TABS_ID, designSelectorTab));

    if (!shouldShowLoader() && isLoaderVisible()) {
      hideLoader();
    }

    if (shouldShowLoader() && !isDesignGallerySelected()) {
      showLoader();

      window.dispatchEvent(
        new CustomEvent(LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT, {
          detail: {
            onDesignLoadedCallback: (): void => {
              getLoaderHTML()?.classList.add('_hidden');
              if (isMyDesignsSelected()) {
                setAreMyDesignsLoaded(true);
              } else if (isTeamSelected()) {
                setAreTeamSpacesDesignsLoaded(true);
              }
            },
          },
        })
      );
    }

    if (shouldShowLoader() && isDesignGallerySelected()) {
      hideLoader();
    }
  }, [designSelectorTab]);

  useEffect(() => {
    dispatch(selectTab(DESIGN_SELECTOR_ELEMENT_TABS_ID, designSelectorTab));
  }, []);

  const getTabItems = (): TabItemContentStorage[] => {
    return [
      {
        tabKey: DesignSelectorTab.MY_DESIGNS,
        text: window.i18next.t('pmwjs_my_designs'),
      },
      {
        tabKey: DesignSelectorTab.TEAM_SPACES,
        text: window.i18next.t('pmwjs_team'),
        showPremiumCrown: isUserPAYG,
        disableSelection: isUserPAYG,
      },
      {
        tabKey: DesignSelectorTab.GALLERY_TEMPLATES,
        text: window.i18next.t('pmwjs_design_templates'),
      },
    ];
  };

  const isDesignAlreadySelected = (designId: string): boolean => {
    if (!selectedDesignsArrayRef.current.includes(designId)) {
      return false;
    }

    return true;
  };

  const shouldShowCheckmarkOnSelection = (): boolean => {
    return isMobile();
  };

  const isMobile = (): boolean => {
    if (paramsRef.current?.small) {
      return true;
    }

    return false;
  };

  const addCheckIconToAllDesigns = (): void => {
    const elements = document.querySelectorAll('[data-id]');

    elements.forEach((element) => {
      const designId = element.getAttribute('data-id');
      const hasUnselectedCheck = document.querySelector(`[data-unselected-check="${designId}"]`) !== null;
      const hasSelectedCheck = document.querySelector(`[data-selected-check="${designId}"]`) !== null;

      if (!(hasUnselectedCheck || hasSelectedCheck)) {
        const checkIcon = getSelectionCheckIcon(designId || '', true);
        appendIconInElement(checkIcon, element);
      }
    });
  };

  const getMyDesignsLength = (): number => {
    return document.querySelectorAll('[data-id]').length;
  };

  const onScroll = (): void => {
    if (shouldShowCheckmarkOnSelection()) {
      addCheckIconToAllDesigns();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll, {passive: true});

    return (): void => {
      window.removeEventListener('scroll', onScroll);
    };
  });

  const getIconElement = (defaultCheck: boolean, designId: string): ReactElement => {
    const iconType = defaultCheck ? IconType.SECONDARY : IconType.PRIMARY;
    const iconSize = isMobile() ? IconSize.SIZE_ICON_16 : IconSize.SIZE_ICON_20;

    return (
      <Icon
        data-selected-check={defaultCheck ? designId : ''}
        data-unselected-check={!defaultCheck ? designId : ''}
        className={`${styles.checkIcon} ${defaultCheck ? styles.unselected : ''}`}
        type={iconType}
        shape={IconShape.SQUARE}
        size={iconSize}
        icon="icon-check"
      />
    );
  };

  const getSelectionCheckIcon = (designId: string, defaultCheck: boolean): ReactElement => {
    return getIconElement(defaultCheck, designId);
  };

  const removeDefaultStyles = (designId: string): void => {
    const element = document.querySelector(`[data-id="${designId}"]`);

    if (!element) {
      return;
    }

    if (isMobile()) {
      element.classList.add(styles.posterItemMobile);
    } else {
      element.classList.add(styles.posterItem);
    }
  };

  const appendIconInElement = (icon: ReactElement, element: Element): void => {
    const wrapper = document.createElement('div');

    const root = createRoot(wrapper);
    root.render(icon);

    element?.appendChild(wrapper);
  };

  const removeSelectionOverlayIfNeeded = (designId: string): void => {
    if (!isMobile()) {
      return;
    }

    const overlayElement = $(`[overlay-selection="${designId}"]`);
    const previewContainer = $(`[data-id="${designId}"]`).children().eq(0);

    previewContainer.removeClass(styles.overlayWrapperClasses);
    overlayElement.remove();
  };

  const updateCheckIconOnSelection = (designId: string): void => {
    const unselectedCheck = document.querySelector(`[data-unselected-check="${designId}"]`);
    if (unselectedCheck?.parentNode) {
      unselectedCheck.parentNode.removeChild(unselectedCheck);
    }

    const element = document.querySelector(`[data-id="${designId}"]`);
    const checkIcon = getSelectionCheckIcon(designId, false);

    if (element) {
      appendIconInElement(checkIcon, element);
    }
  };

  const updateCheckIconOnDeselection = (designId: string): void => {
    const selectedCheck = document.querySelector(`[data-selected-check="${designId}"]`);

    if (selectedCheck?.parentNode) {
      selectedCheck.parentNode.removeChild(selectedCheck);
    }

    const element = document.querySelector(`[data-id="${designId}"]`);

    const checkIcon = getSelectionCheckIcon(designId, true);

    if (element) {
      appendIconInElement(checkIcon, element);
    }
  };

  const showDesignsSelectedCount = (): boolean => {
    const isWeb = !shouldShowCheckmarkOnSelection();
    return isWeb || showSelectionCountForMobile;
  };

  const addStylesOnSelection = (designId: string): void => {
    const element = $(`[data-id="${designId}"]`);
    const previewInsideElement = $(`[data-id="${designId}"] .preview-container`).eq(0);
    const nameInsideElement = $(`[data-id="${designId}"] .name`).eq(0);
    removeDefaultStyles(designId);

    if (shouldShowCheckmarkOnSelection()) {
      const checkIcon = getSelectionCheckIcon(designId, false);
      const elementHTML = element.get(0);
      if (elementHTML) {
        appendIconInElement(checkIcon, elementHTML);
      }
    }

    if (showDesignsSelectedCount()) {
      const newDiv = $('<div>').text('1').attr('selected-id', designId);
      newDiv.addClass(`${styles.numberTag} ${isMobile() ? styles.small : ''}`);
      element.append(newDiv);

      previewInsideElement.addClass(styles.selectedClass);
      nameInsideElement.addClass(styles.selectedName);
    } else {
      updateCheckIconOnSelection(designId);
    }
  };

  const removeStylesOnDeselection = (designId: string): void => {
    const previewInsideElement = $(`[data-id="${designId}"] .preview-container`).eq(0);
    const nameInsideElement = $(`[data-id="${designId}"] .name`).eq(0);
    const selectorElement = $(`[selected-id="${designId}"]`);
    const checkElement = $(`[data-selected-check="${designId}"]`);

    removeDefaultStyles(designId);
    previewInsideElement.removeClass(styles.selectedClass);
    nameInsideElement.removeClass(styles.selectedName);
    selectorElement.remove();

    if (showDesignsSelectedCount()) {
      checkElement.remove();
    } else {
      updateCheckIconOnDeselection(designId);
    }

    removeSelectionOverlayIfNeeded(designId);
  };

  const updateSelectors = (): void => {
    for (let i = 0; i < selectedDesignsArrayRef.current.length; i++) {
      const selectorElement = $(`[selected-id="${selectedDesignsArrayRef.current[i]}"]`);
      selectorElement.text(`${i + 1}`);
    }
  };

  const onDesignClick = (designId: string): void => {
    if (!designId) {
      return;
    }

    if (isDesignAlreadySelected(designId)) {
      selectedDesignsArrayRef.current = selectedDesignsArrayRef.current.filter((a) => {
        return a !== designId;
      });

      removeStylesOnDeselection(designId);
      updateSelectors();
      onDesignListChange(selectedDesignsArrayRef.current);
      return;
    }

    selectedDesignsArrayRef.current = [...selectedDesignsArrayRef.current, designId];
    onDesignListChange(selectedDesignsArrayRef.current);
    addStylesOnSelection(designId);
    updateSelectors();
  };

  const loadMyDesignsDesignSelector = (designsLoadedCallback: () => void): void => {
    loadDesignSelector(designsLoadedCallback, onDesignClick, '.js-design-selector-element', LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT, startDesignFromUploadBtnOpts);
  };

  const onTabClick = (tabKey: TabKey): void => {
    if ((tabKey as DesignSelectorTab) === DesignSelectorTab.MY_DESIGNS) {
      updateTabSelection(DesignSelectorTab.MY_DESIGNS);
    } else if ((tabKey as DesignSelectorTab) === DesignSelectorTab.GALLERY_TEMPLATES) {
      updateTabSelection(DesignSelectorTab.GALLERY_TEMPLATES);
    } else if (isUserPAYG) {
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEAMS);
    } else {
      updateTabSelection(DesignSelectorTab.TEAM_SPACES);
    }
  };

  const getClassesForDesktopUpperContainer = (): string => {
    return `${styles.upperContainerDesktop} ${isDesignGallerySelected() ? 'spacing-m-b-0' : ''}`;
  };

  const getDesktopUpperContainer = (): ReactElement => {
    return (
      <div id="design-selector-onboarding-container" className={getClassesForDesktopUpperContainer()}>
        <Tabs className="spacing-m-b-4" id={DESIGN_SELECTOR_ELEMENT_TABS_ID} tabItems={getTabItems()} selectedTab={designSelectorTab} onTabClick={onTabClick} />
      </div>
    );
  };

  const getDropdownText = (): string => {
    if (isMyDesignsSelected()) {
      return window.i18next.t('pmwjs_my_designs');
    }

    if (isTeamSelected()) {
      return window.i18next.t('pmwjs_team');
    }

    return window.i18next.t('pmwjs_design_gallery');
  };

  const getClassesForMobileUpperContainer = (): string => {
    return `${styles.upperContainerMobile} ${isDesignGallerySelected() ? 'spacing-m-b-0' : ''}`;
  };

  const getMobileUpperContainer = (): ReactElement => {
    return (
      <div className={getClassesForMobileUpperContainer()}>
        <Text val={window.i18next.t('pmwjs_select_a_design_lowercase')} className={`${styles.selectADesignTextMobile} _unmargin}`} />
        <Dropdown
          className={styles.designSelectorMobileDropdown}
          selector={<DropdownDefaultSelectBox id="design-selector-dropdown-1" text={getDropdownText()} size={DROPDOWN_SIZE.SMALL} isLoading={false} />}
          popup={
            <div style={{width: '100%'}}>
              <ControlledList
                id="design-selector-dropdown-list"
                items={[
                  {
                    id: 'design-selector-dropdown-2',
                    text: window.i18next.t('pmwjs_my_designs'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    isSelected: isMyDesignsSelected(),
                    onClick: (): void => {
                      updateTabSelection(DesignSelectorTab.MY_DESIGNS);
                    },
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-3',
                    text: window.i18next.t('pmwjs_team'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: (): void => {
                      updateTabSelection(DesignSelectorTab.TEAM_SPACES);
                    },
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-4',
                    text: window.i18next.t('pmwjs_design_gallery'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: (): void => {
                      updateTabSelection(DesignSelectorTab.GALLERY_TEMPLATES);
                    },
                    showSelectionCheckmark: false,
                  },
                ]}
              />
            </div>
          }
        />
      </div>
    );
  };

  const getUpperContainer = (): ReactElement | null => {
    if (hideTabContainer) {
      return null;
    }

    return params.small ? getMobileUpperContainer() : getDesktopUpperContainer();
  };

  const getFolderLoader = (): ReactElement => {
    let folderLoaderCount = 0;
    let rightMarginString = '';

    if (skeletonParams.xxsmall) {
      folderLoaderCount = 1;
    } else if (skeletonParams.xsmall) {
      folderLoaderCount = 2;
      rightMarginString = 'spacing-m-r-3';
    } else if (skeletonParams.small) {
      folderLoaderCount = 3;
      rightMarginString = 'spacing-m-r-4';
    } else if (skeletonParams.medium || skeletonParams.large) {
      folderLoaderCount = 2;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.xlarge || skeletonParams.xxlarge) {
      folderLoaderCount = 3;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.xxxlarge) {
      folderLoaderCount = 4;
      rightMarginString = 'spacing-m-r-6';
    }
    const skeletonArray: ReactElement[] = [];
    for (let i = 0; i < folderLoaderCount; i++) {
      const isLastFolder = i === folderLoaderCount - 1;
      skeletonArray.push(<SkeletonBrick isPulsating className={`radius-2 ${isLastFolder ? '' : rightMarginString}`} height={56} key={`design-selector-skeleton-${i}`} />);
    }
    return <div className={`spacing-m-b-3 flex-h-row ${styles.foldersSkeleton}`}>{skeletonArray}</div>;
  };

  const getPosterLoader = (): ReactElement => {
    let posterLoaderCount = 0;
    let rightMarginString = '';
    if (skeletonParams.xxsmall) {
      posterLoaderCount = 1;
    } else if (skeletonParams.xsmall || skeletonParams.small) {
      posterLoaderCount = 3;
      rightMarginString = 'spacing-m-r-5';
    } else if (skeletonParams.medium) {
      posterLoaderCount = 2;
      rightMarginString = 'spacing-m-r-6';
    } else if (skeletonParams.large || skeletonParams.xlarge) {
      posterLoaderCount = 3;
      rightMarginString = 'spacing-m-r-6';
    } else if (skeletonParams.xxlarge || skeletonParams.xxxlarge) {
      posterLoaderCount = 4;
      rightMarginString = 'spacing-m-r-5';
    }

    const skeletonArray: ReactElement[] = [];
    for (let i = 0; i < posterLoaderCount; i++) {
      const isLastPoster = i === posterLoaderCount - 1;

      skeletonArray.push(
        <div className={isLastPoster ? '' : rightMarginString} style={{width: '100%'}} key={`design-selector-poster-skeleton-${i}`}>
          <SkeletonBrick isPulsating className="spacing-m-b-4" height={148} />
          <SkeletonBrick isPulsating height={20} width={136} />
        </div>
      );
    }
    return <div className="flex-h-row">{skeletonArray}</div>;
  };

  const getClassesForLoaderWrapper = (): string => {
    return `${styles.loaderWrapperMystuff} ${isDesignGallerySelected() ? '_hidden' : ''}`;
  };

  const getLoader = (): ReactElement => {
    return (
      <div className={`${getClassesForLoaderWrapper()} ${SKELETON_LOADER}`}>
        <div className={styles.designSelectorLoaderDesktop}>
          {getFolderLoader()}
          <div className={styles.viewMoreButtonSkeletonContainer}>
            <SkeletonBrick isPulsating className={`radius-4 ${styles.viewMoreButtonSkeleton}`} width={80} height={20} />
          </div>
          {getPosterLoader()}
        </div>
      </div>
    );
  };

  const getDesignSelectorLoadingClass = (): string => {
    if (isLoading) {
      return styles.isLoadingDesignSelector;
    }

    return '';
  };

  const getDesignSelectorDesktopClasses = (): string => {
    return `${styles.designSelectorDesktop} ${desktopClasses}`;
  };

  const getDesignSelectorMobileClasses = (): string => {
    return `${styles.designSelectorMobile} ${mobileClasses}`;
  };

  const getDesignSelectorClasses = (): string => {
    let classes = getDesignSelectorLoadingClass();

    if (params.small) {
      classes += ` ${getDesignSelectorMobileClasses()}`;
    } else {
      classes += ` ${getDesignSelectorDesktopClasses()}`;
    }

    if (isDesignGallerySelected()) {
      classes += ` ${styles.templateGallerySelector}`;
    }

    return classes;
  };

  const getDesignSelector = (): ReactElement => {
    return (
      <div className={getDesignSelectorClasses()}>
        {getUpperContainer()}
        {getLoader()}
        <div className={`js-design-selector-element design-selector-element ${styles.designSelectorElementContent} ${isMyDesignsSelected() || isTeamSelected() ? '' : '_hidden'}`}>
          <div className={`mystuff-posters ${isMyDesignsSelected() ? 'selected' : '_hidden'} ${myDesignsLength ? '' : styles.emptyMyDesigns}`} />
          <div className={`teamspace-posters ${isTeamSelected() ? 'selected' : '_hidden'}`} />
        </div>

        <div className={`${styles.galleryTemplateSelector} ${isDesignGallerySelected() ? '' : '_hidden'}`}>
          <DesignGallery disableCreateFromScratchCTA onDesignSelected={onDesignSelectionCallback} height={designGalleryHeight} startingPageURI={designGalleryStartingURI} />
        </div>
      </div>
    );
  };

  return <div ref={containerRef}>{getDesignSelector()}</div>;
}
